import React, { useCallback, useEffect, useState } from 'react';
import PropTypes from "prop-types";
import withRouter from "../../components/Common/withRouter";
import { Modal, ModalHeader, ModalBody, Alert } from "reactstrap";

// import Components
import Sidebar from './Sidebar';
import Header from './Header';
import Footer from './Footer';
import RightSidebar from '../../components/Common/RightSideBar';
import PWAInstallBanner from '../../components/Common/PWAInstallBanner';
import LicenceExpirationBanner from '../../components/Common/LicenceExpirationBanner';

//redux
import { useSelector, useDispatch } from "react-redux";

import {
  changeLayout,
  changeSidebarTheme,
  changeSidebarType,
  changeTopbarTheme,
  changeLayoutWidth,
  showRightSidebarAction
} from "../../store/actions";
import { setCurrentCompany } from "../../store/companies/actions";

const MIN_SUPPORTED_VERSION = "1.2.170";
const VERSION_URL = "https://www.ticketcontrol.net/version.json";
const UPGRADE_EXE_VERSION = "1.2.141";
const WHATSAPP_PHONE = "542236769606";
const WHATSAPP_MESSAGE = "Hola ! necesito ayuda para actualizar TicketControl.";

// ── Gate CAEA (RG 5782) ────────────────────────────────────────────────
// Desde el 1/8/2026 ARCA exige la contingencia CAEA (TicketControl 2.0.60+).
// Las versiones anteriores usan el "comprobante provisorio", fuera de norma.
// UMBRAL: los estacionamientos de ARGENTINA con Facturación Electrónica
// activa y versión < 2.0.60 quedan bloqueados de Monitor (palanca de
// migración: presiona al dueño sin tocar la facturación).
// INTERRUPTOR: el gate recién se ARMA cuando version.json del sitio publica
// >= 2.0.60 (el día del aviso masivo). Así este deploy es inerte hasta que
// se edite version.json — y futuras versiones (2.0.61+) NO bloquean a los
// que ya están en 2.0.60 (el umbral es la constante, no "la última").
const CAEA_MIN_VERSION = "2.0.60";
// Escalera de presión: false = solo BANNER superior de aviso;
// true = PANTALLA DE BLOQUEO total. Cambiar a true y deployar cuando se
// decida activar el bloqueo (el banner desaparece solo).
// ACTIVADO 2026-08-07 (decisión de Diego): fase banner cumplida desde el
// 29/07; deadline normativo 1/8 ya pasado.
const CAEA_BLOCK_ENABLED = true;
const CAEA_GUIDE_URL = "https://www.ticketcontrol.net/arca-contingencia";
const CAEA_VIDEO_URL = "https://youtu.be/JawAMkp6sCg"; // Video paso a paso

function compareVersions(v1, v2) {
  if (!v1 || !v2) return 0;
  const a = v1.split(".").map(Number);
  const b = v2.split(".").map(Number);
  for (let i = 0; i < Math.max(a.length, b.length); i++) {
    const n1 = a[i] || 0;
    const n2 = b[i] || 0;
    if (n1 > n2) return 1;
    if (n1 < n2) return -1;
  }
  return 0;
}

function isVersionBelow(current, minimum) {
  if (!current || current === "0.0.0") return false; // aún no cargó, no bloquear
  const a = current.split(".").map(Number);
  const b = minimum.split(".").map(Number);
  for (let i = 0; i < Math.max(a.length, b.length); i++) {
    const n1 = a[i] || 0;
    const n2 = b[i] || 0;
    if (n1 < n2) return true;
    if (n1 > n2) return false;
  }
  return false;
}

const Layout = props => {
  const dispatch = useDispatch();

  const {
    layoutWidth,
    leftSideBarType,
    topbarTheme,
    showRightSidebar,
    leftSideBarTheme,
  } = useSelector(state => ({
    leftSideBarType: state.Layout.leftSideBarType,
    layoutWidth: state.Layout.layoutWidth,
    topbarTheme: state.Layout.topbarTheme,
    showRightSidebar: state.Layout.showRightSidebar,
    leftSideBarTheme: state.Layout.leftSideBarTheme,
  }));

  const versionDB = useSelector(state => state.data.versionDB);
  const appConfig = useSelector(state => state.data.appConfig);
  const companies = useSelector(state => state.companies.companies);
  const currentCompany = useSelector(state => state.companies.currentCompany);
  const [versionNews, setVersionNews] = useState(null);
  const [showHowToUpdate, setShowHowToUpdate] = useState(false);
  // Versión publicada en version.json — es el INTERRUPTOR del gate CAEA.
  // Si el fetch falla, queda null → gate desarmado (fail-open: jamás dejar
  // a un dueño sin Monitor por un problema de red nuestro).
  const [remoteVersion, setRemoteVersion] = useState(null);

  useEffect(() => {
    // Query param único + no-store: saltea caché del navegador Y del CDN —
    // el interruptor tiene que verse al instante, sin ventana de max-age.
    const checkRemoteVersion = () =>
      fetch(`${VERSION_URL}?t=${Date.now()}`, { cache: "no-store" })
        .then((res) => res.json())
        .then((data) =>
          // Solo string: cualquier otro tipo desarma el gate (fail-open)
          // en vez de romper compareVersions en pleno render.
          setRemoteVersion(typeof data?.version === "string" ? data.version : null)
        )
        .catch(() => {});
    checkRemoteVersion();
    // Re-consulta cada 5 min: pestañas que quedaron abiertas se arman solas
    // el día del cambio de version.json, y un rollback también las desarma
    // sin necesidad de F5. Además, al VOLVER a la pestaña (visibilitychange)
    // se consulta al instante — cubre el uso "pestaña siempre abierta".
    const interval = setInterval(checkRemoteVersion, 5 * 60 * 1000);
    const onVisible = () => {
      if (document.visibilityState === "visible") checkRemoteVersion();
    };
    document.addEventListener("visibilitychange", onVisible);
    return () => {
      clearInterval(interval);
      document.removeEventListener("visibilitychange", onVisible);
    };
  }, []);

  useEffect(() => {
    if (!versionDB || versionDB === "0.0.0") return;
    if (!isVersionBelow(versionDB, MIN_SUPPORTED_VERSION)) return;
    fetch(VERSION_URL)
      .then((res) => res.json())
      .then((data) => setVersionNews(data))
      .catch(() => {});
  }, [versionDB]);

  const isMobile = /iPhone|iPad|iPod|Android/i.test(navigator.userAgent);

  const toggleMenuCallback = () => {
    if (leftSideBarType === "default") {
      dispatch(changeSidebarType("condensed", isMobile));
    } else if (leftSideBarType === "condensed") {
      dispatch(changeSidebarType("default", isMobile));
    }
  };

  //hides right sidebar on body click
  const hideRightbar = useCallback((event) => {
    var rightbar = document.getElementById("right-bar");
    //if clicked in inside right bar, then do nothing
    if (rightbar && rightbar.contains(event.target)) {
      return;
    } else {
      //if clicked in outside of rightbar then fire action for hide rightbar
      dispatch(showRightSidebarAction(false));
    }
  }, [dispatch]);

  /*
  layout  settings
  */

  useEffect(() => {
    //init body click event fot toggle rightbar
    document.body.addEventListener("click", hideRightbar, true);
  }, [hideRightbar]);

  // Scroll al top en el mount inicial y cada vez que cambia la ruta
  // (sin esto, al cambiar de menú queda el scroll de la página anterior).
  useEffect(() => {
    window.scrollTo(0, 0);
  }, [props.router?.location?.pathname]);

  useEffect(() => {
    dispatch(changeLayout("vertical"));
  }, [dispatch]);

  useEffect(() => {
    if (leftSideBarTheme) {
      dispatch(changeSidebarTheme(leftSideBarTheme));
    }
  }, [leftSideBarTheme, dispatch]);

  useEffect(() => {
    if (layoutWidth) {
      dispatch(changeLayoutWidth(layoutWidth));
    }
  }, [layoutWidth, dispatch]);

  useEffect(() => {
    if (leftSideBarType) {
      dispatch(changeSidebarType(leftSideBarType));
    }
  }, [leftSideBarType, dispatch]);

  useEffect(() => {
    if (topbarTheme) {
      dispatch(changeTopbarTheme(topbarTheme));
    }
  }, [topbarTheme, dispatch]);

  // ── Gate CAEA (RG 5782): Argentina + módulo Factura Electrónica + versión
  // vieja → bloquear Monitor hasta que actualicen a 2.0.60.
  // Solo actúa cuando version.json publica >= 2.0.60 (interruptor).
  const caeaGateArmed =
    remoteVersion && compareVersions(remoteVersion, CAEA_MIN_VERSION) >= 0;
  // versionDB ya es version_app con fallback a version_db (ListenerDB) —
  // misma fuente que el gate de versión mínima de abajo.
  const caeaAppVersion = versionDB || "0.0.0";
  // Alcanzado por la normativa: SOLO Argentina + módulo Factura Electrónica
  // activo + versión < 2.0.60 (y el interruptor remoto armado).
  const caeaAffected =
    caeaGateArmed &&
    appConfig?.modules?.invoices === 1 &&
    // Sin country cargado se asume ARGENTINA (misma convención que PlateFormat:
    // los clientes más viejos, justo los del gate, no tienen el campo).
    (currentCompany?.country || "ARGENTINA").trim().toUpperCase() === "ARGENTINA" &&
    isVersionBelow(caeaAppVersion, CAEA_MIN_VERSION);
  const caeaGateBlocked = CAEA_BLOCK_ENABLED && caeaAffected;
  const caeaBannerVisible = caeaAffected && !CAEA_BLOCK_ENABLED;

  if (caeaGateBlocked) {
    return (
      <div
        className="d-flex justify-content-center align-items-center"
        style={{ minHeight: "100vh", background: "#f8f9fa", padding: 24 }}
      >
        <div className="text-center" style={{ maxWidth: 520 }}>
          <span
            className="material-symbols-rounded d-block mb-3"
            style={{ fontSize: 56, color: "#dc3545" }}
          >
            gavel
          </span>
          <h4 className="mb-3">
            Actualización obligatoria por normativa de ARCA
          </h4>
          <p className="text-muted" style={{ fontSize: 14 }}>
            Desde el <strong>1 de agosto de 2026</strong>, la Resolución
            General <strong>5782 de ARCA</strong> exige que ante cortes de
            conexión las facturas se emitan con el sistema de contingencia{" "}
            <strong>CAEA</strong>. La versión de TicketControl instalada en
            {currentCompany?.name ? ` "${currentCompany.name}"` : " este estacionamiento"}
            {caeaAppVersion !== "0.0.0" ? ` (v${caeaAppVersion})` : ""} todavía
            usa el comprobante provisorio, que quedó fuera de la normativa.
          </p>
          <p className="text-muted" style={{ fontSize: 14 }}>
            Para volver a usar Monitor y facturar dentro de la normativa,
            actualizá TicketControl a la versión{" "}
            <strong>{CAEA_MIN_VERSION} o superior</strong> y configurá la
            contingencia CAEA. Son 3 pasos simples y tu información no se
            pierde.
          </p>
          <Alert color="warning" className="py-2 px-3 text-start" style={{ fontSize: 13 }}>
            <strong>1.</strong> Crear el punto de venta CAEA en el sitio de
            ARCA (uno por cada punto de venta/estacionamiento).
            <br />
            <strong>2.</strong>{" "}
            {compareVersions(caeaAppVersion, UPGRADE_EXE_VERSION) >= 0
              ? 'Actualizar TicketControl (botón "Actualizar" en la app).'
              : "Actualizar TicketControl (tu versión no tiene actualización automática: escribinos por WhatsApp y te ayudamos a hacerlo sin perder información)."}
            <br />
            <strong>3.</strong> Configurarlo en TicketControl (Configuración →
            Módulos → Factura Electrónica).
          </Alert>
          <div className="d-flex flex-column gap-2 mt-3">
            <a
              href={CAEA_VIDEO_URL}
              target="_blank"
              rel="noopener noreferrer"
              className="btn btn-danger d-flex align-items-center justify-content-center gap-2"
            >
              <span className="material-symbols-rounded" style={{ fontSize: 18 }}>
                play_circle
              </span>
              Ver el video paso a paso
            </a>
            <a
              href={CAEA_GUIDE_URL}
              target="_blank"
              rel="noopener noreferrer"
              className="btn btn-primary d-flex align-items-center justify-content-center gap-2"
            >
              <span className="material-symbols-rounded" style={{ fontSize: 18 }}>
                menu_book
              </span>
              Ver la guía paso a paso
            </a>
          </div>
          {companies && companies.length > 1 && (
            <>
              <hr />
              <p className="text-muted mb-2" style={{ fontSize: 13 }}>
                Cambiar de estacionamiento:
              </p>
              <div className="d-flex flex-wrap justify-content-center gap-2">
                {companies.map((c) => (
                  <button
                    key={c.id}
                    className={`btn btn-sm ${c.id === currentCompany?.id ? "btn-primary" : "btn-outline-secondary"}`}
                    onClick={() => dispatch(setCurrentCompany(c))}
                  >
                    {c.name}
                  </button>
                ))}
              </div>
            </>
          )}
          <hr />
          <a href="/logout" className="text-muted" style={{ fontSize: 13 }}>
            Cerrar sesión
          </a>
        </div>
      </div>
    );
  }

  // Block access if desktop version is too old
  if (isVersionBelow(versionDB, MIN_SUPPORTED_VERSION)) {
    return (
      <div
        className="d-flex justify-content-center align-items-center"
        style={{ minHeight: "100vh", background: "#f8f9fa", padding: 24 }}
      >
        <div className="text-center" style={{ maxWidth: 480 }}>
          <span
            className="material-symbols-rounded d-block mb-3"
            style={{ fontSize: 56, color: "#dc3545" }}
          >
            browser_updated
          </span>
          <h4 className="mb-3">Monitor no disponible para esta versión</h4>
          <p className="text-muted" style={{ fontSize: 14 }}>
            El software TicketControl instalado en el estacionamiento
            {currentCompany?.name ? ` "${currentCompany.name}"` : ""}
            {" "}(v{versionDB}) es una versión muy antigua y ya no es compatible
            con Monitor.
          </p>
          <p className="text-muted" style={{ fontSize: 14 }}>
            Para seguir utilizando Monitor, es necesario actualizar
            TicketControl en el estacionamiento.
          </p>
          {companies && companies.length > 1 && (
            <>
              <hr />
              <p className="text-muted mb-2" style={{ fontSize: 13 }}>
                Cambiar de estacionamiento:
              </p>
              <div className="d-flex flex-wrap justify-content-center gap-2">
                {companies.map((c) => (
                  <button
                    key={c.id}
                    className={`btn btn-sm ${c.id === currentCompany?.id ? "btn-primary" : "btn-outline-secondary"}`}
                    onClick={() => dispatch(setCurrentCompany(c))}
                  >
                    {c.name}
                  </button>
                ))}
              </div>
            </>
          )}
          {versionNews && (() => {
            const newsSince = (versionNews.news || []).filter(
              (n) => compareVersions(n.version, versionDB) > 0
            );
            if (!newsSince.length) return null;
            return (
              <>
                <hr />
                <p className="text-muted mb-3" style={{ fontSize: 13, fontWeight: 600 }}>
                  Estas son algunas de las mejoras que incluyen las nuevas versiones:
                </p>
                <div className="text-start" style={{ maxHeight: 300, overflowY: "auto" }}>
                  {newsSince.map((release) => (
                    <div key={release.version} className="mb-3">
                      <div className="mb-1">
                        <span
                          className="badge bg-secondary"
                          style={{ fontSize: 11 }}
                        >
                          v{release.version}
                        </span>
                      </div>
                      {(release.details || []).map((detail, idx) => (
                        <div
                          key={idx}
                          className="ms-2 mb-1 ps-2"
                          style={{ borderLeft: "2px solid #dee2e6" }}
                        >
                          <div style={{ fontWeight: 600, fontSize: 12 }}>
                            {detail.title}
                          </div>
                        </div>
                      ))}
                    </div>
                  ))}
                </div>
              </>
            );
          })()}
          <hr />
          <button
            className="btn btn-primary d-inline-flex align-items-center gap-2"
            onClick={() => setShowHowToUpdate(true)}
          >
            <span className="material-symbols-rounded" style={{ fontSize: 18 }}>
              help
            </span>
            Cómo actualizar
          </button>
          <Modal isOpen={showHowToUpdate} toggle={() => setShowHowToUpdate(false)} centered scrollable>
            <ModalHeader toggle={() => setShowHowToUpdate(false)}>
              <span
                className="material-symbols-rounded me-2"
                style={{ fontSize: 22, verticalAlign: "middle" }}
              >
                help
              </span>
              Cómo actualizar TicketControl
            </ModalHeader>
            <ModalBody>
              {compareVersions(versionDB, UPGRADE_EXE_VERSION) >= 0 ? (
                <div>
                  <div className="d-flex align-items-start mb-2">
                    <span
                      className="material-symbols-rounded me-2 text-success flex-shrink-0"
                      style={{ fontSize: 22, marginTop: 2 }}
                    >
                      system_update_alt
                    </span>
                    <div>
                      <h6 className="mb-2">Actualización automática</h6>
                      <p style={{ fontSize: 13, lineHeight: 1.6 }}>
                        En la PC del estacionamiento, abrí <strong>TicketControl</strong> y
                        verás un botón <strong>"Actualizar"</strong> en la parte inferior de la pantalla.
                        Solo tenés que presionarlo.
                      </p>
                      <p style={{ fontSize: 13, lineHeight: 1.6 }}>
                        El programa se cerrará automáticamente, descargará la nueva versión
                        y se abrirá el instalador. Seguí los pasos hasta el final.
                        Luego, abrí TicketControl normalmente y listo.
                      </p>
                      <Alert color="success" className="py-2 px-3 mb-0" style={{ fontSize: 12 }}>
                        <span className="material-symbols-rounded me-1" style={{ fontSize: 15, verticalAlign: "middle" }}>
                          verified
                        </span>
                        <strong>Tu información no se pierde.</strong> Todos tus tickets, configuraciones,
                        cuentas corrientes y datos permanecen intactos después de la actualización.
                      </Alert>
                    </div>
                  </div>
                </div>
              ) : (
                <div>
                  <div className="d-flex align-items-start mb-3">
                    <span
                      className="material-symbols-rounded me-2 text-warning flex-shrink-0"
                      style={{ fontSize: 22, marginTop: 2 }}
                    >
                      support_agent
                    </span>
                    <div>
                      <h6 className="mb-2">Necesitás ayuda de Soporte</h6>
                      <p style={{ fontSize: 13, lineHeight: 1.6 }}>
                        La versión instalada en tu estacionamiento es muy antigua y no cuenta
                        con el sistema de actualización automática. Nuestro equipo de soporte
                        te ayudará a actualizar de forma segura sin perder tu información.
                      </p>
                    </div>
                  </div>
                  <a
                    href={`https://wa.me/${WHATSAPP_PHONE}?text=${encodeURIComponent(WHATSAPP_MESSAGE)}`}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="btn btn-success w-100 d-flex align-items-center justify-content-center gap-2"
                  >
                    <i className="ri-whatsapp-line" style={{ fontSize: 18 }}></i>
                    Contactar a Soporte por WhatsApp
                  </a>
                </div>
              )}
            </ModalBody>
          </Modal>
        </div>
      </div>
    );
  }

  return (
    <React.Fragment>
      <div id="layout-wrapper">
        <Header toggleMenuCallback={toggleMenuCallback} />
        <Sidebar
          theme={leftSideBarTheme}
          type={leftSideBarType}
          isMobile={isMobile}
        />
        <div className="main-content">
          {caeaBannerVisible && (
            <Alert
              color="warning"
              className="mb-0 rounded-0 py-2 px-3"
              // Queda pegado bajo el topbar fijo (70px). El margen negativo
              // devuelve ese espacio: .page-content ya trae su propio
              // padding-top, así el contenido conserva su separación normal.
              style={{
                marginTop: 70,
                marginBottom: -70,
                borderLeft: "4px solid",
                fontSize: 13,
                position: "relative",
                zIndex: 1,
              }}
            >
              <div className="d-flex align-items-center justify-content-between gap-2 flex-wrap">
                <div style={{ minWidth: 0 }}>
                  <span
                    className="material-symbols-rounded me-1"
                    style={{ fontSize: 16, verticalAlign: "middle" }}
                  >
                    gavel
                  </span>
                  <strong>Actualización obligatoria — normativa ARCA (RG 5782).</strong>{" "}
                  Desde el <strong>1/8</strong> la versión instalada
                  {caeaAppVersion !== "0.0.0" ? ` (v${caeaAppVersion})` : ""} queda
                  fuera de norma al facturar sin conexión. Actualizá TicketControl a
                  la <strong>2.0.60</strong>.
                </div>
                <div className="d-flex gap-2 flex-shrink-0">
                  <a
                    href={CAEA_VIDEO_URL}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="btn btn-danger btn-sm"
                    style={{ whiteSpace: "nowrap", fontSize: 12 }}
                  >
                    <i className="ri-play-circle-line me-1" style={{ verticalAlign: "middle" }}></i>
                    Ver Video
                  </a>
                  <a
                    href={CAEA_GUIDE_URL}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="btn btn-warning btn-sm"
                    style={{ whiteSpace: "nowrap", fontSize: 12 }}
                  >
                    Ver Guía Paso a Paso
                  </a>
                </div>
              </div>
            </Alert>
          )}
          {props.children}
        </div>
        {/* IdmDuplicateBanner queda fuera desde 2.0.80. Agrupaba los estacionamientos
            del dueño por `monitor_id` y, con dos o más en el mismo grupo, le ofrecía
            "Quitar de mi cuenta". Pero el monitor_id vive dentro de ticketControl.db y
            el export para sucursal (exportPartialRestore) nunca limpió app_config: cada
            sucursal dada de alta a partir de un export nace con el mismo identificador.
            Es decir que a un dueño con dos sucursales legítimas el banner le decía que
            eran la misma PC y lo invitaba a sacarse una de encima. El componente se
            conserva sin usar para poder volver atrás; lo reemplaza la detección por
            huella de hardware, que se mira desde soporte y nunca desde el cliente. */}
        <LicenceExpirationBanner />
        <Footer />
      </div>
      {showRightSidebar ? <RightSidebar /> : null}
      <PWAInstallBanner />
    </React.Fragment>
  );
};

Layout.propTypes = {
  changeLayoutWidth: PropTypes.func,
  changeSidebarTheme: PropTypes.func,
  changeSidebarType: PropTypes.func,
  changeTopbarTheme: PropTypes.func,
  children: PropTypes.object,
  layoutWidth: PropTypes.any,
  leftSideBarTheme: PropTypes.any,
  leftSideBarType: PropTypes.any,
  location: PropTypes.object,
  showRightSidebar: PropTypes.any,
  topbarTheme: PropTypes.any,
};

export default withRouter(Layout);
